import { useSearchParams } from 'react-router-dom'
import {
  Users, Target, Swords, GraduationCap,
  Check, ArrowRight, ShieldCheck,
} from 'lucide-react'
import JoinShell from './JoinShell'
import DiscordIcon from '@/components/site/DiscordIcon'
import Reveal from '@/components/site/Reveal'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">
      {children}
    </h2>
  )
}

export default function JoinLanding() {
  const { T } = useLanguage()
  const [params] = useSearchParams()
  const error = params.get('error')
  const errorMsg = error
    ? (T.join.errors[error as keyof typeof T.join.errors] ?? T.join.errors.generic)
    : null

  const pillars = [
    { icon: Users,         title: T.join.pillars.communityTitle, body: T.join.pillars.communityBody },
    { icon: Swords,        title: T.join.pillars.matchesTitle,   body: T.join.pillars.matchesBody },
    { icon: Target,        title: T.join.pillars.drillsTitle,    body: T.join.pillars.drillsBody },
    { icon: GraduationCap, title: T.join.pillars.academyTitle,   body: T.join.pillars.academyBody },
  ]

  const requirements = [
    T.join.requirements.age,
    T.join.requirements.mic,
    T.join.requirements.experience,
    T.join.requirements.attendance,
    T.join.requirements.steam,
  ]

  const steps = [
    { n: '01', title: T.join.steps.one,   body: T.join.steps.oneBody },
    { n: '02', title: T.join.steps.two,   body: T.join.steps.twoBody },
    { n: '03', title: T.join.steps.three, body: T.join.steps.threeBody },
    { n: '04', title: T.join.steps.four,  body: T.join.steps.fourBody },
    { n: '05', title: T.join.steps.five,  body: T.join.steps.fiveBody },
  ]

  const faq = [
    { q: T.join.faq.q1, a: T.join.faq.a1 },
    { q: T.join.faq.q2, a: T.join.faq.a2 },
    { q: T.join.faq.q3, a: T.join.faq.a3 },
    { q: T.join.faq.q4, a: T.join.faq.a4 },
  ]

  return (
    <JoinShell showNav>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-5 pb-16 pt-16 md:pb-24 md:pt-24">
        {errorMsg && (
          <div className="mb-8 rounded-sm border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
            {errorMsg}
          </div>
        )}

        <p className="mb-4 inline-flex items-center gap-2 rounded-sm border border-primary/40 bg-primary/10 px-2.5 py-1 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {T.join.heroKicker}
        </p>

        <h1 className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight md:text-7xl">
          {T.join.heroTitle}
        </h1>

        <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground/80 md:text-lg">
          {T.join.heroLead}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a href="/api/join/auth/login">
            <Button size="lg" className="gap-3 font-semibold transition-transform active:scale-95">
              <DiscordIcon />
              {T.join.applyCta}
            </Button>
          </a>
          <a href="#how">
            <Button size="lg" variant="outline" className="gap-2">
              {T.join.scrollHint}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-[12px] text-muted-foreground/70">
          <ShieldCheck className="h-3.5 w-3.5" />
          {T.login.securedOAuth}
        </p>
      </section>

      {/* ── Pillars ──────────────────────────────────────────────────────── */}
      <section id="what" className="scroll-mt-14 border-t border-white/10 bg-black/25 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-5xl px-5 py-14 md:py-20">
          <Reveal><SectionTitle>{T.join.pillars.title}</SectionTitle></Reveal>
          <div className="mt-8 grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={100 + i * 110} className="bg-card/80 p-5">
                <p.icon className="h-5 w-5 text-primary" />
                <h3 className="mt-3.5 font-display text-base font-bold tracking-tight">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Requirements + steps ─────────────────────────────────────────── */}
      <section className="border-t border-white/10">
        <div className="mx-auto grid w-full max-w-5xl gap-12 px-5 py-14 md:py-20 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          {/* Requirements */}
          <div id="expect" className="scroll-mt-20">
            <Reveal><SectionTitle>{T.join.requirements.title}</SectionTitle></Reveal>
            <ul className="mt-7 space-y-3">
              {requirements.map((r, i) => (
                <li key={r}>
                  <Reveal delay={100 + i * 90} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-primary/40 bg-primary/10">
                      <Check className="h-3 w-3 text-primary" />
                    </span>
                    <span className="text-sm leading-relaxed text-foreground/85">{r}</span>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>

          {/* Steps */}
          <div id="how" className="scroll-mt-20">
            <Reveal><SectionTitle>{T.join.steps.title}</SectionTitle></Reveal>
            <ol className="mt-7 space-y-5 border-l border-white/12 pl-6">
              {steps.map((s, i) => (
                <li key={s.n}>
                  <Reveal delay={100 + i * 110} className="relative">
                    <span className="absolute -left-[31px] top-0.5 flex h-[22px] w-[22px] items-center justify-center rounded-sm border border-white/15 bg-card font-display text-[10px] font-bold text-primary">
                      {s.n}
                    </span>
                    <h3 className="font-display text-[15px] font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{s.body}</p>
                  </Reveal>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="scroll-mt-14 border-t border-white/10 bg-black/25 backdrop-blur-sm">
        <div className="mx-auto w-full max-w-5xl px-5 py-14 md:py-20">
          <Reveal><SectionTitle>{T.join.faq.title}</SectionTitle></Reveal>
          <dl className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {faq.map((f, i) => (
              <Reveal key={f.q} delay={100 + i * 110}>
                <dt className="font-display text-[15px] font-bold tracking-tight">{f.q}</dt>
                <dd className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{f.a}</dd>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="border-t border-white/10">
        <Reveal className="mx-auto flex w-full max-w-5xl flex-col items-center gap-6 px-5 py-16 text-center md:py-20">
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
            {T.join.heroTitle}
          </h2>
          <a href="/api/join/auth/login">
            <Button size="lg" className="gap-3 font-semibold transition-transform active:scale-95">
              <DiscordIcon />
              {T.join.applyCta}
            </Button>
          </a>
        </Reveal>
      </section>
    </JoinShell>
  )
}
